import googleMapsService from "../../Services/GoogleMapsService";

// Locating guides on a map.
//
// Guides have no coordinate fields — the `Guide` model is just
// title/description/images. The AI engine *does* resolve each place through
// Google Places when it generates a guide, but it only writes the address
// into the description text:
//
//   <place name>
//   <blurb>
//   ⭐ Rated 4.5/5 (1200 reviews)
//   📍 Location: 1600 Amphitheatre Pkwy, Mountain View, CA 94043, USA
//   📞 Phone: ...
//   🌐 Website: ...
//
// So the address is recovered from that line and geocoded in the browser.
// The proper fix is persisting lat/lng on the guide at generation time —
// the engine already has them — which would make this module unnecessary.
// Until then, results are cached hard so a guest pays the lookup once.

const CACHE_KEY = "lb_guide_geocode_v1";
const CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
// Addresses change rarely, so a long TTL keeps repeat visits free. Geocoding
// is billed per request, and a city guide set can be dozens of places.

// The engine writes the address on a single line, so a newline is the real
// terminator. "Located at" is also matched for older hand-written guides.
const ADDRESS_REGEX = /(?:📍\s*Location:|Located at)\s*([^\n]+)/i;

/**
 * Pulls the street address out of a guide description, or "" if absent.
 * Shared with GuideDrawer so the pin and the drawer's address button can
 * never disagree about where a place is.
 */
export const extractGuideAddress = (description) => {
  if (typeof description !== "string" || !description) return "";

  const match = description.match(ADDRESS_REGEX);
  if (!match) return "";

  // Trim trailing punctuation the model sometimes appends, but keep commas
  // inside the address itself.
  return match[1].trim().replace(/[.;,]+$/, "").trim();
};

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    // Private mode, quota, or corrupt JSON — behave as a cold cache.
    return {};
  }
};

const writeCache = (cache) => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // Nothing to do: caching is an optimisation, not a requirement.
  }
};

const cacheKeyFor = (address) => address.trim().toLowerCase();

/**
 * Geocodes a list of `{ id, address }` entries into
 * `{ [id]: { lat, lng, formattedAddress, placeId } }`.
 *
 * Addresses resolved before are served from localStorage. New ones go out a
 * few at a time: firing a whole city guide set at once reliably trips
 * OVER_QUERY_LIMIT, and a rejected batch would leave the map half-empty.
 * Failures are recorded as misses so one bad address can't block the rest.
 *
 * `isCancelled` lets the caller abandon in-flight work on unmount.
 */
export const geocodeGuideAddresses = async (
  entries,
  { concurrency = 4, isCancelled = () => false } = {}
) => {
  const results = {};
  if (!Array.isArray(entries) || entries.length === 0) return results;

  const cache = readCache();
  const now = Date.now();
  let cacheDirty = false;

  const pending = [];

  entries.forEach((entry) => {
    const address = entry?.address;
    if (!entry?.id || typeof address !== "string" || !address) return;

    const key = cacheKeyFor(address);
    const hit = cache[key];

    if (hit && now - hit.t < CACHE_TTL_MS) {
      // `null` position is a remembered failure — skip it without re-billing.
      if (hit.p) results[entry.id] = hit.p;
      return;
    }

    pending.push({ id: entry.id, address, key });
  });

  for (let i = 0; i < pending.length; i += concurrency) {
    if (isCancelled()) break;

    const slice = pending.slice(i, i + concurrency);

    // Batches run sequentially on purpose: the geocoder rate-limits, and a
    // whole city guide set fired at once returns OVER_QUERY_LIMIT.
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(
      slice.map(async ({ id, address, key }) => {
        try {
          const position = await googleMapsService.geocodeAddress({ address });
          if (
            position &&
            typeof position.lat === "number" &&
            typeof position.lng === "number"
          ) {
            results[id] = position;
            cache[key] = { t: now, p: position };
          } else {
            cache[key] = { t: now, p: null };
          }
        } catch (error) {
          // A single unresolvable address shouldn't cost the whole map.
          console.warn(`Could not locate guide address: ${address}`, error);
          cache[key] = { t: now, p: null };
        }
      })
    );

    // Every attempt records an outcome, successful or not.
    cacheDirty = true;
  }

  if (cacheDirty) writeCache(cache);

  return results;
};

/**
 * Narrows a guide list to the ones that advertise an address, preserving
 * order. Returned items keep the original guide under `guide`.
 */
export const guidesWithAddresses = (guides) => {
  if (!Array.isArray(guides)) return [];

  return guides.reduce((acc, guide) => {
    const address = extractGuideAddress(guide?.description);
    if (address) acc.push({ id: guide.id, address, guide });
    return acc;
  }, []);
};
